import { getIn } from 'formik';
import { TweenLite } from 'gsap';
import { filter, find, forEach, round, sortBy, sumBy } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import styled from 'styled-components/macro';
import { useOnChange } from '../../../../helpers/hooks/useOnChange';
import { useOnMount } from '../../../../helpers/hooks/useOnMount';
import { useTemporaryBoolean } from '../../../../helpers/hooks/useTemporaryBoolean';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { ReactComponent as VisibilityIcon } from '../../../../images/icons/visibility.svg';
import { ReactComponent as VisibilityOffIcon } from '../../../../images/icons/visibility_off.svg';
import { FieldErrorIndicator } from '../../../../shared/form/FieldErrorIndicator';
import { FormContext } from '../../../../shared/form/Form';
import { SelectOptions } from '../../../../shared/form/inputs/Select';
import { FastTextInputField } from '../../../../shared/form/inputs/TextInputField';
import { ReadOnlyField } from '../../../../shared/form/ReadOnlyField';
import { spacing03, spacing04, spacing05 } from '../../../../styling/spacing';
import { DesignCalculationsRequest } from '../../../designs/design-form/calculations/designCalculationsResponse';
import { DesignMaterialsCalculationsRequest } from '../../../designs/design-form/calculations/materials/designMaterialsCalculationsResponse';
import { AnimateInContext } from '../AnimateInContext';
import { DeleteAndDuplicateButtons, DuplicatedSuccessMessage, IconButton } from '../DeleteAndDuplicateButtons';
import { DeleteConfirmation } from '../DeleteConfirmation';
import { DragHandle } from '../drag-and-drop/DragHandle';
import { PumpFormModel } from '../GetDataForQuoteOrDesignForm';
import { ManifoldFormModel } from '../manifolds/manifoldFormModel';
import { TreeContextProvider } from '../map/map-helpers/hooks/useTree';
import { MapCanvas } from '../map/MapCanvas';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { QuoteOrDesignFormExpandControlChevron } from '../QuoteOrDesignFormExpandControlChevron';
import { manifoldFieldName, unitFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { RoomFormModel } from '../rooms/roomFormModel';
import { SectionHeader } from '../SectionHeader';
import { getDefaultCylinderVolumeOrNull } from './cylinderVolumes';
import { DesignFormModeButtons } from './DesignFormModeButtons';
import { UnitHeatLoss } from './heatLoss/UnitHeatLoss';
import { UnitDetailsFieldsRow } from './UnitDetailsFieldsRow';
import { UnitFieldContext, UnitFieldContextValue } from './UnitFieldContext';
import {
  DesignFormMode,
  unitCanHaveHeatLossInformation,
  UnitFormModel,
  unitHasHeatPump,
  unitHasSkirtingHeating,
  unitHasSolar,
  unitMustHaveAPumpOnEveryManifold,
} from './unitFormModel';
import { UnitManifoldsContainer } from './UnitManifoldsContainer';

type Props = {
  unit: UnitFormModel;
  unitIndex: number;
  isExpanded: boolean;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
  deleteUnit: () => void;
  duplicateUnit: () => void;
  designCalculationsRequest?: DesignCalculationsRequest;
  designMaterialsCalculationsRequest?: DesignMaterialsCalculationsRequest;
  onUnitFocus: () => void;
};

export const UnitFields = ({
  unit,
  unitIndex,
  isExpanded,
  dragHandleProps,
  deleteUnit,
  duplicateUnit,
  designCalculationsRequest,
  designMaterialsCalculationsRequest,
  onUnitFocus,
}: Props) => {
  const {
    formType,
    referenceData: { unitPumpOptions },
    formProps: { setFieldValue, errors, touched, values },
  } = assertNotNull(useContext(QuoteOrDesignFormContext));

  const unitIsFocused = values.focusedUnitId != null;
  const isInReadOnlyMode = useContext(FormContext).isDisabled;

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [shouldShowDuplicatedSuccessMessage, showDuplicatedSuccessMessage] = useTemporaryBoolean();
  const [formMode, setFormMode] = useState<DesignFormMode>('Design');

  const onClickDuplicate = () => {
    duplicateUnit();
    showDuplicatedSuccessMessage();
  };

  const container = useRef<HTMLDivElement | null>(null);
  const animateInContext = useContext(AnimateInContext);

  useOnMount(() => {
    if (!animateInContext.hasAnimated(unit.id)) {
      if (container.current != null) {
        TweenLite.set(container.current, { opacity: 1, backgroundColor: 'transparent' });
        TweenLite.from(container.current, 2, { opacity: 0, backgroundColor: 'white' });
      }
      animateInContext.setAnimated(unit.id);
    }
  });

  // We automatically set the 'cylinderVolumeInLitres' field based on the number of Bedrooms/Bathrooms, and whether the Unit has Heat Pump and/or Solar
  const hasHeatPump = unitHasHeatPump(unit);
  const hasSolar = unitHasSolar(unit);
  useOnChange(() => {
    const bedrooms = Number(unit.numberOfBedrooms);
    const bathrooms = Number(unit.numberOfBathrooms);

    if (!isNaN(bedrooms) && !isNaN(bathrooms)) {
      const defaultCylinderVolume = getDefaultCylinderVolumeOrNull(bedrooms, bathrooms, hasHeatPump, hasSolar);

      setFieldValue(
        unitFieldName(unitIndex, 'cylinderVolumeOption'),
        defaultCylinderVolume ? defaultCylinderVolume.toString() : null,
      );
    }
  }, [unit.numberOfBedrooms, unit.numberOfBathrooms, hasHeatPump, hasSolar]);

  const totalUnderfloorHeatingAreaInMetresSquared = sumBy(unit.manifolds, (manifold: ManifoldFormModel) =>
    Number(manifold.totalUnderfloorHeatingAreaInMetresSquared),
  );

  useEffect(() => {
    if (!isNaN(totalUnderfloorHeatingAreaInMetresSquared)) {
      setFieldValue(
        unitFieldName(unitIndex, 'totalUnderfloorHeatingAreaInMetresSquared'),
        totalUnderfloorHeatingAreaInMetresSquared,
      );
    }
  }, [totalUnderfloorHeatingAreaInMetresSquared]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasSkirtingHeating = unitHasSkirtingHeating(unit);
  // When pump selection type is 'Automatic' we automatically choose the most appropriate value for the
  // 'pumps' field based on the total area of the Unit, the heat source type and whether the unit has a heat pump or skirting heating
  useOnChange(() => {
    if (
      isNaN(totalUnderfloorHeatingAreaInMetresSquared) ||
      unit.pumpLocation !== 'RemotePump' ||
      unit.pumpSelectionType !== 'Automatic' ||
      unit.hasPipelineMap
    ) {
      return;
    }

    const nonNullUnitPumpOptions = filter(unitPumpOptions, option => option.value != null) as SelectOptions<
      PumpFormModel
    >;

    const suitableUnitPumpOptions = filter(
      nonNullUnitPumpOptions,
      option =>
        unit.heatSourceTypeCode === 'HeatPump'
          ? !option.value.includesBlendingValve // Use "W" pumps only
          : option.value.includesBlendingValve, // Use "P" pumps only
    );

    const bestPump =
      find(
        sortBy(suitableUnitPumpOptions, option => option.value.maxAreaInMetresSquared),
        option => option.value.maxAreaInMetresSquared >= totalUnderfloorHeatingAreaInMetresSquared,
      )?.value ?? null;

    if (!hasSkirtingHeating) {
      setFieldValue(unitFieldName(unitIndex, 'pumps'), [bestPump]);
    } else {
      const skirtingHeatingDefaultPumpName = 'W2560SC7-A';

      const skirtingHeatingDefaultAdditionalPump =
        find(nonNullUnitPumpOptions, option => option.value.displayName === skirtingHeatingDefaultPumpName)?.value ??
        null;

      const unitIsOnlySkirtingOrElectricUnderfloorHeating = totalUnderfloorHeatingAreaInMetresSquared === 0;

      setFieldValue(
        unitFieldName(unitIndex, 'pumps'),
        skirtingHeatingDefaultAdditionalPump == null
          ? [unitIsOnlySkirtingOrElectricUnderfloorHeating ? null : bestPump]
          : unitIsOnlySkirtingOrElectricUnderfloorHeating
          ? [skirtingHeatingDefaultAdditionalPump]
          : [bestPump, skirtingHeatingDefaultAdditionalPump],
      );
    }
  }, [
    totalUnderfloorHeatingAreaInMetresSquared,
    unit.pumpLocation,
    unit.pumpSelectionType,
    hasSkirtingHeating,
    unit.heatSourceTypeCode,
    unit.hasPipelineMap,
  ]);

  const mustHaveAPumpOnEveryManifold = unitMustHaveAPumpOnEveryManifold(unit, formType);

  // When pump selection type is 'Automatic' we automatically choose the most appropriate value for the
  // 'pumpLocation' field based on the heat source type, the total number of ports and the number of manifolds on the unit
  useOnChange(() => {
    if (unit.hasPipelineMap || unit.pumpSelectionType !== 'Automatic') {
      return;
    }

    const newPumpLocation =
      unit.heatSourceTypeCode === 'HeatPump'
        ? 'RemotePump'
        : mustHaveAPumpOnEveryManifold || unit.manifolds.length < 2
        ? 'PumpOnManifold'
        : 'RemotePump';

    setFieldValue(unitFieldName(unitIndex, 'pumpLocation'), newPumpLocation);

    if (newPumpLocation === 'PumpOnManifold') {
      setFieldValue(unitFieldName(unitIndex, 'pumps'), [null]);
    }

    if (newPumpLocation === 'RemotePump') {
      forEach(unit.manifolds, (manifold, index) => {
        setFieldValue(manifoldFieldName(unitIndex, index, 'pump'), null);
      });
    }
  }, [unit.pumpSelectionType, unit.heatSourceTypeCode, unit.manifolds.length, mustHaveAPumpOnEveryManifold]);

  useOnChange(() => {
    // if we're changing hasPipelineMap to false, then we need to wipe the map
    if (formType === 'Design' && !unit.hasPipelineMap) {
      setFieldValue(unitFieldName(unitIndex, 'pipelineMapJson'), null);
    }

    // if we're changing it to true, then we need to ditch the pumps so that the tree can add them itself
    if (formType === 'Design' && unit.hasPipelineMap) {
      setFieldValue(unitFieldName(unitIndex, 'pumps'), []);
      setFieldValue(unitFieldName(unitIndex, 'pumpSelectionType'), 'Automatic');
    }
  }, [unit.hasPipelineMap]);

  const unitBaseFieldName = `units.${unitIndex}`;
  const hasNestedError = getIn(errors, unitBaseFieldName) && getIn(touched, unitBaseFieldName);

  const hasHeatLossDetails = unit.heatLossDetails != null;

  const designCalculationsResult = (designCalculationsRequest && designCalculationsRequest.response) || null;

  const unitDesignCalculationsResponse =
    (designCalculationsResult && find(designCalculationsResult.units, u => u.unitIndex === unitIndex)) || null;

  const totalAreaInMetresSquared = sumBy(unit.manifolds, (manifold: ManifoldFormModel) =>
    sumBy(manifold.rooms, (room: RoomFormModel) => Number(room.areaInMetresSquared)),
  );

  const totalCoilCount =
    formType === 'Design'
      ? sumBy(unit.manifolds, (manifold: ManifoldFormModel) =>
          sumBy(manifold.rooms, (room: RoomFormModel) =>
            !isNaN(Number(room.designRoomDetails?.pipesPerPort)) && !isNaN(Number(room.numberOfPorts))
              ? Number(room.designRoomDetails?.pipesPerPort ?? 0) * Number(room.numberOfPorts ?? 0)
              : 0,
          ),
        )
      : null;

  const unitFieldContextValue: UnitFieldContextValue = {
    unit,
    unitIndex,
    designCalculations:
      designCalculationsRequest == null
        ? null
        : {
            response: unitDesignCalculationsResponse,
            inProgress: designCalculationsRequest.inProgress,
            error: designCalculationsRequest.error,
          },
    totalAreaInMetresSquared,
  };

  return (
    <UnitFieldContext.Provider value={unitFieldContextValue}>
      <div ref={container} style={{ padding: `${spacing04}` }}>
        <SectionHeader>
          <DragHandle dragHandleProps={dragHandleProps} disabled={isInReadOnlyMode} />
          {hasNestedError && (
            <NestedErrorsIndicator
              fieldName={`units.${unitIndex}`}
              showError={hasNestedError}
              error="Some fields within this Unit are invalid"
            />
          )}
          <FastTextInputField name={unitFieldName(unitIndex, 'name')} label={null} />
          <QuoteOrDesignFormExpandControlChevron
            onClick={() => {
              setFieldValue(unitFieldName(unitIndex, 'isExpanded'), !isExpanded);
            }}
            isExpanded={isExpanded}
            testId={unitExpandControlChevronTestId(unitIndex)}
          />
          {!isInReadOnlyMode && (
            <>
              <DeleteAndDuplicateButtons
                onClickDelete={() => setIsConfirmingDelete(true)}
                onClickDuplicate={onClickDuplicate}
                deleteButtonTestId={unitDeleteButtonTestId(unitIndex)}
                disabled={unitIsFocused}
              />
              <IconButton onClick={onUnitFocus} title={!unitIsFocused ? 'Focus Unit' : 'Unfocus Unit'}>
                {!unitIsFocused ? <VisibilityIcon title="Focus Unit" /> : <VisibilityOffIcon title="Unfocus Unit" />}
              </IconButton>
              <DuplicatedSuccessMessage $visible={shouldShowDuplicatedSuccessMessage}>
                Duplicated
              </DuplicatedSuccessMessage>
            </>
          )}
          {formType === 'Design' && unit.hasPipelineMap && (
            <SectionHeaderRight>
              <DesignFormModeButtons selectedMode={formMode} setMode={setFormMode} unitIndex={unitIndex} />
            </SectionHeaderRight>
          )}
        </SectionHeader>
        <DeleteConfirmation
          isConfirmingDelete={isConfirmingDelete}
          setIsConfirmingDelete={setIsConfirmingDelete}
          deleteItem={deleteUnit}
          deleteButtonTestId={unitConfirmDeleteButtonTestId(unitIndex)}
        />
        {formMode === 'Design' ? (
          <>
            <AdditionalInformationRow>
              {formType === 'Design' &&
                unitCanHaveHeatLossInformation(unit) &&
                (!isInReadOnlyMode || hasHeatLossDetails) && <UnitHeatLoss />}
              <AdditionalInformationField label="Coils" value={totalCoilCount ? totalCoilCount.toString() : '-'} />
              <AdditionalInformationField
                label={
                  <>
                    Total UFH Area (m<sup>2</sup>)
                  </>
                }
                value={round(totalUnderfloorHeatingAreaInMetresSquared, 1).toLocaleString()}
                testId={totalUnderfloorHeatingAreaFieldTestId(unitIndex)}
              />
            </AdditionalInformationRow>
            <UnitDetailsFieldsRow />
            <UnitManifoldsContainer
              isExpanded={isExpanded}
              designMaterialsCalculationsRequest={designMaterialsCalculationsRequest}
            />
          </>
        ) : (
          formMode === 'Map' && (
            <TreeContextProvider>
              <MapCanvas manifolds={unit.manifolds} />
            </TreeContextProvider>
          )
        )}
      </div>
    </UnitFieldContext.Provider>
  );
};
export const unitExpandControlChevronTestId = (unitIndex: number) => `unit-${unitIndex}-expand-control-chevron`;

export const unitDeleteButtonTestId = (unitIndex: number) => `unit-${unitIndex}-delete-button`;

export const unitConfirmDeleteButtonTestId = (unitIndex: number) => `unit-${unitIndex}-confirm-delete-button`;

export const addManifoldButtonTestId = (unitIndex: number) => `unit-${unitIndex}-add-manifold`;

export const addRadiatorManifoldButtonTestId = (unitIndex: number) => `unit-${unitIndex}-add-radiator-manifold`;

export const addHeatPumpButtonTestId = (unitIndex: number) => `unit-${unitIndex}-add-heat-pump-button`;

export const deleteUnitPumpButtonTestId = (unitIndex: number, pumpIndex: number) =>
  `unit-${unitIndex}-delete-unit-pump-button-${pumpIndex}`;

export const addUnitPumpButtonTestId = (unitIndex: number) => `unit-${unitIndex}-add-unit-pump-button`;

export const totalUnderfloorHeatingAreaFieldTestId = (unitIndex: number) =>
  `unit-${unitIndex}-total-underfloor-heating-area`;

export const addSolarButtonTestId = (unitIndex: number) => `unit-${unitIndex}-add-solar-button`;

const NestedErrorsIndicator = styled(FieldErrorIndicator)`
  margin-right: ${spacing03};
`;

const SectionHeaderRight = styled.div`
  display: flex;
  flex-grow: 100;
  justify-content: end;
`;

const AdditionalInformationRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
  gap: 12px;
`;

const AdditionalInformationField = styled(ReadOnlyField)`
  margin-bottom: ${spacing05};
`;
